<script setup lang="ts">
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { ActivitiesRecord, BookingsRecord, GuestUsersRecord, ReferrersRecord, pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";

const userLoggedin = computed(() => pb.authStore.isValid);

interface Props {
  modelValue?: boolean;
  booking: BookingsRecord;
  activities: ActivitiesRecord;
  referrers?: ReferrersRecord[];
};

const props = withDefaults(defineProps<Props>(), {
  referrers: () => [],
})

const emit = defineEmits(["closeDialog"]);

const selectedReferrer = ref("");
const subscribeToNewsletter = ref(true);
const showNewsletterCheckbox = ref(false);
const existingGuestUserId = ref<string | null>(null);
const linkedUserId = ref<string | null>(null);

const globalSetupStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(globalSetupStore);


// Format booking time
const formatBookingTime = computed(() => {
  if (!props.booking?.startTime) return "";

  const bookingDate = moment(props.booking.startTime);
  const timeStr = bookingDate.format("h:mm A");

  if (bookingDate.isSame(moment(), "day")) {
    return `${timeStr} Today`;
  } else if (bookingDate.isSame(moment().add(1, "day"), "day")) {
    return `${timeStr} Tomorrow`;
  } else {
    return `${timeStr} on ${bookingDate.format("dddd, MMMM Do")}`;
  }
});

// Update booking referrer
const updateBookingReferrer = (input: string | null) => {
  selectedReferrer.value = input ?? "";
};

const buildContactFilter = (email?: string, phone?: string) => {
  const parts: string[] = [];
  if (email) parts.push(`email="${email.toLowerCase()}"`);
  if (phone) parts.push(`mobileNumber="${phone}"`);
  return parts.join(" || ");
};

const closeDialog = async () => {
  const email = props.booking?.visitorEmail;
  const phone = props.booking?.visitorPhone;
  if (email || phone) {
    try {
      const guestData: Partial<GuestUsersRecord> = {
        name: props.booking.visitors || "",
      };
      if (email) guestData.email = email.toLowerCase();
      if (phone) guestData.mobileNumber = phone;
      if (showNewsletterCheckbox.value) {
        guestData.subscribed = subscribeToNewsletter.value;
        guestData.askedAboutNewsletters = true;
      }

      if (existingGuestUserId.value) {
        await pb.collection("guestUsers").update(existingGuestUserId.value, guestData);
      } else {
        if (linkedUserId.value) guestData.user = linkedUserId.value;
        await pb.collection("guestUsers").create(guestData);
      }
    } catch (e) {
      console.error("Failed to save guestUser newsletter preference:", e);
    }
  }
  emit("closeDialog", selectedReferrer.value);
};

onMounted(async () => {
  const email = props.booking?.visitorEmail;
  const phone = props.booking?.visitorPhone;
  if (userLoggedin.value || (!email && !phone)) return;

  // Look up existing guestUser and linked registered user (by email or mobileNumber) in parallel
  const guestFilter = buildContactFilter(email, phone);
  const userFilter = buildContactFilter(email, phone);

  const [guestResult, userResult] = await Promise.allSettled([
    pb.collection("guestUsers").getFirstListItem(guestFilter),
    pb.collection("users").getFirstListItem(userFilter),
  ]);

  if (guestResult.status === "fulfilled") {
    existingGuestUserId.value = guestResult.value.id;
    if (!guestResult.value.askedAboutNewsletters) {
      subscribeToNewsletter.value = guestResult.value.subscribed ?? true;
    } else {
      return; // Already asked — don't show checkbox
    }
  }

  if (userResult.status === "fulfilled") {
    linkedUserId.value = userResult.value.id;
  }

  if (globalSetup.value?.useNewsletters) {
    showNewsletterCheckbox.value = true;
  }
});

const referrerOptions = computed(() =>
  // Transform referrers array into options format
  props.referrers.map((referrer) => ({
    title: `${referrer.name}${referrer.notes !== "" ? ` - ${referrer.notes}` : ""}`,
    value: `${referrer.name}${referrer.notes !== "" ? ` - ${referrer.notes}` : ""}`,
    disabled: !referrer.enabled,
  })),
);
</script>

<template>
  <VDialog :model-value="props.modelValue" persistent max-width="600px">
    <VCard>
      <VCardTitle class="text-h5 pa-4">
        {{ activities?.name }} Booking Confirmed
      </VCardTitle>

      <VCardText>
        <!-- Confirmation Message -->
        <VRow>
          <VCol cols="12">
            <div class="text-body-1">Confirmation code: {{ booking.id }}</div>
          </VCol>
        </VRow>
        <VRow>
          <VCol cols="12">
            <div class="text-body-1">
              Your {{ activities?.name }} booking is confirmed for {{ formatBookingTime }}
            </div>
            <div class="text-body-2 mt-2" v-if="booking.owner || booking?.users?.length > 0">
              An email will be sent to you with these details.
            </div>
            <div class="text-body-2 mt-2" v-else>
              If you have provided an email address we will email you the details.
            </div>
          </VCol>
        </VRow>

        <!-- Email -->
        <VRow>
          <VCol cols="12">
            <strong>Your booking is confirmed regardless of the email being able to be delivered</strong>
          </VCol>
        </VRow>

        <!-- Booking Agreement -->
        <VRow>
          <VCol cols="12">
            <div class="text-caption text-grey-darken-1">
              {{ activities?.bookingAgreement }}
            </div>
          </VCol>
        </VRow>

        <!-- Newsletter Subscribe -->
        <VRow class="mt-2 mb-2" v-if="showNewsletterCheckbox">
          <VCol cols="12">
            <VCheckbox
              v-model="subscribeToNewsletter"
              label="Subscribe to our newsletter"
              hide-details
            />
          </VCol>
        </VRow>

        <!-- Referral Section -->
        <VRow class="mt-4 mb-4" v-if="!userLoggedin">
          <VCol cols="12">
            <div class="text-h6">How did you hear about us?</div>
            <div class="text-subtitle-2 mb-4">
              Please take a second to indicate how you heard about our venue
            </div>
            <VCombobox
              :items="referrerOptions"
              :item-props="{ title: 'title', value: 'value' }"
              label="Select or type your answer"
              :return-object="false"
              clearable
              @update:model-value="updateBookingReferrer"
            >
              <template v-slot:item="{ props, item }">
                <VListItem v-bind="props" :title="item.raw.title" />
              </template>
            </VCombobox>
          </VCol>
        </VRow>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn color="primary" variant="flat" @click="closeDialog">Close</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
<style scoped lang="scss">
.v-dialog > .v-overlay__content > .v-card > .v-card-text {
  padding: 0 20px;
}
</style>
